import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, MessageSquare } from "lucide-react";
import { api } from "@/lib/api";

export function AdminJournalList() {
    const [entries, setEntries] = useState([]);
    const [users, setUsers] = useState([]);
    const [userId, setUserId] = useState("");
    const [mode, setMode] = useState("");
    const [outcome, setOutcome] = useState("");
    const [unrepliedOnly, setUnrepliedOnly] = useState(false);
    const [loading, setLoading] = useState(true);

    async function load() {
        setLoading(true);
        try {
            const [e, u] = await Promise.all([
                api.adminListJournalEntries({
                    user_id: userId || undefined,
                    mode: mode || undefined,
                    outcome: outcome || undefined,
                    unreplied_only: unrepliedOnly ? "true" : undefined,
                    limit: 100,
                }),
                api.adminListJournalUsers(),
            ]);
            setEntries(e.entries);
            setUsers(u.users);
        } finally { setLoading(false); }
    }
    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

    return (
        <section className="max-w-6xl mx-auto px-6 py-10 space-y-6" data-testid="admin-journal-page">
            <div>
                <div className="tracking-caps text-[11px] font-semibold" style={{ color: "var(--st-royal)" }}>Journal review</div>
                <h1 className="font-serif text-4xl mt-2" style={{ color: "var(--st-navy)" }} data-testid="admin-journal-heading">
                    Student trades
                </h1>
                <p className="mt-2 text-sm" style={{ color: "var(--st-ink-muted)" }}>
                    Filter, review, and leave feedback on any student's journal entry.
                </p>
            </div>

            <form
                onSubmit={(e) => { e.preventDefault(); load(); }}
                className="st-surface p-4 grid gap-3 md:grid-cols-[1fr_120px_120px_auto_auto] items-end"
                data-testid="admin-journal-filters"
            >
                <div>
                    <label className="st-label">Student</label>
                    <select className="st-input" value={userId} onChange={(e) => setUserId(e.target.value)} data-testid="filter-student">
                        <option value="">All students</option>
                        {users.map((u) => (
                            <option key={u.user_id} value={u.user_id}>{`${u.name} (${u.email})`}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="st-label">Mode</label>
                    <select className="st-input" value={mode} onChange={(e) => setMode(e.target.value)} data-testid="filter-mode">
                        <option value="">All</option>
                        <option value="paper">Paper</option>
                        <option value="live">Live</option>
                    </select>
                </div>
                <div>
                    <label className="st-label">Outcome</label>
                    <select className="st-input" value={outcome} onChange={(e) => setOutcome(e.target.value)} data-testid="filter-outcome">
                        <option value="">All</option>
                        <option value="open">Open</option>
                        <option value="win">Win</option>
                        <option value="loss">Loss</option>
                        <option value="breakeven">Breakeven</option>
                    </select>
                </div>
                <label className="flex items-center gap-2 text-sm pb-2" style={{ color: "var(--st-navy)" }}>
                    <input type="checkbox" checked={unrepliedOnly} onChange={(e) => setUnrepliedOnly(e.target.checked)} data-testid="filter-unreplied" />
                    Unreplied only
                </label>
                <button type="submit" className="btn-royal text-sm" data-testid="filter-apply">Apply</button>
            </form>

            <div className="st-surface overflow-hidden" data-testid="admin-journal-entries-table">
                <table className="w-full text-sm">
                    <thead>
                        <tr style={{ background: "var(--st-silver-50)", color: "var(--st-ink-muted)" }}>
                            <th className="text-left px-4 py-3 font-semibold tracking-caps text-[10px]">Student</th>
                            <th className="text-left px-4 py-3 font-semibold tracking-caps text-[10px]">Ticker</th>
                            <th className="text-left px-4 py-3 font-semibold tracking-caps text-[10px]">Date</th>
                            <th className="text-left px-4 py-3 font-semibold tracking-caps text-[10px]">Mode</th>
                            <th className="text-left px-4 py-3 font-semibold tracking-caps text-[10px]">Outcome</th>
                            <th className="text-right px-4 py-3 font-semibold tracking-caps text-[10px]">Comments</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && <tr><td colSpan={7} className="text-center py-6" style={{ color: "var(--st-silver-500)" }}>Loading…</td></tr>}
                        {!loading && entries.length === 0 && <tr><td colSpan={7} className="text-center py-6 italic" style={{ color: "var(--st-silver-500)" }}>No entries match.</td></tr>}
                        {entries.map((e) => (
                            <tr key={e.id} style={{ borderTop: "1px solid var(--st-line)" }} data-testid={`admin-entry-row-${e.id}`}>
                                <td className="px-4 py-3">
                                    <div className="font-semibold" style={{ color: "var(--st-navy)" }}>{e.user_summary.name}</div>
                                    <div className="text-xs" style={{ color: "var(--st-silver-500)" }}>{e.user_summary.email}</div>
                                </td>
                                <td className="px-4 py-3 font-semibold" style={{ color: "var(--st-navy)" }}>{e.ticker}</td>
                                <td className="px-4 py-3">{new Date(e.entry_date).toLocaleDateString()}</td>
                                <td className="px-4 py-3 tracking-caps text-[11px] font-semibold">{e.mode}</td>
                                <td className="px-4 py-3 tracking-caps text-[11px] font-semibold">{e.outcome}</td>
                                <td className="px-4 py-3 text-right">{e.comment_count}</td>
                                <td className="px-4 py-3 text-right">
                                    <Link to={`/admin/journal/${e.id}`} className="link-royal text-sm" data-testid={`admin-review-btn-${e.id}`}>
                                        Review →
                                    </Link>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </section>
    );
}

export function AdminJournalReview() {
    const { entryId } = useParams();
    const [entry, setEntry] = useState(null);
    const [comments, setComments] = useState([]);
    const [body, setBody] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const navigate = useNavigate();

    async function reload() {
        const e = await api.getJournalEntry(entryId);
        setEntry(e);
        const c = await api.listJournalComments(entryId);
        setComments(c.comments);
    }
    useEffect(() => { reload(); /* eslint-disable-next-line */ }, [entryId]);

    async function handleSubmit(e) {
        e.preventDefault();
        if (body.trim().length < 1) return;
        setSubmitting(true);
        try {
            await api.addJournalComment(entryId, body.trim());
            setBody("");
            await reload();
        } finally { setSubmitting(false); }
    }

    if (!entry) return <div className="max-w-3xl mx-auto px-6 py-16">Loading…</div>;

    return (
        <section className="max-w-3xl mx-auto px-6 py-10 space-y-6" data-testid="admin-journal-review">
            <div className="flex items-center gap-2 text-xs" style={{ color: "var(--st-silver-500)" }}>
                <Link to="/admin/journal" className="hover:underline flex items-center gap-1"><ArrowLeft size={12} /> Journal list</Link>
            </div>

            <div className="st-surface p-6" data-testid="admin-review-entry-card">
                <h1 className="font-serif text-3xl" style={{ color: "var(--st-navy)" }}>
                    {entry.ticker} · {entry.direction.toUpperCase()}
                </h1>
                <div className="mt-2 text-sm" style={{ color: "var(--st-ink-muted)" }}>
                    Mode: {entry.mode} · Outcome: <strong>{entry.outcome}</strong> · Entry ${entry.entry_price} → Exit{" "}
                    {entry.exit_price ? `$${entry.exit_price}` : "OPEN"} · P&L{" "}
                    {entry.pnl_percent !== null && entry.pnl_percent !== undefined ? `${entry.pnl_percent.toFixed(2)}%` : "—"}
                </div>
                <div className="mt-4">
                    <div className="tracking-caps text-[10px] font-semibold" style={{ color: "var(--st-silver-500)" }}>Thesis</div>
                    <p className="mt-1 leading-relaxed" style={{ color: "var(--st-ink)" }}>{entry.thesis}</p>
                </div>
                {entry.notes && (
                    <div className="mt-4">
                        <div className="tracking-caps text-[10px] font-semibold" style={{ color: "var(--st-silver-500)" }}>Notes</div>
                        <p className="mt-1 leading-relaxed" style={{ color: "var(--st-ink)" }}>{entry.notes}</p>
                    </div>
                )}
            </div>

            <div className="st-surface p-6" data-testid="admin-review-comments">
                <div className="tracking-caps text-[11px] font-semibold flex items-center gap-2" style={{ color: "var(--st-royal)" }}>
                    <MessageSquare size={12} /> Comments
                </div>
                {comments.length === 0 ? (
                    <p className="mt-3 text-sm italic" style={{ color: "var(--st-silver-500)" }}>No comments yet.</p>
                ) : (
                    <ul className="mt-3 space-y-3">
                        {comments.map((c) => (
                            <li key={c.id} className="p-3 rounded" style={{ background: "var(--st-silver-50)" }}>
                                <div className="text-xs font-semibold" style={{ color: "var(--st-navy)" }}>{c.author_name}</div>
                                <p className="mt-1 text-sm" style={{ color: "var(--st-ink)" }}>{c.body}</p>
                            </li>
                        ))}
                    </ul>
                )}
                <form onSubmit={handleSubmit} className="mt-5 space-y-3" data-testid="admin-comment-form">
                    <textarea rows={3} className="st-input" style={{ height: "auto", padding: "0.5rem 0.9rem" }}
                              value={body} onChange={(e) => setBody(e.target.value)}
                              placeholder="Leave feedback for this trade — decision quality, risk sizing, thesis strength…"
                              data-testid="admin-comment-body" />
                    <div className="flex justify-end">
                        <button type="submit" disabled={submitting || !body.trim()} className="btn-royal text-sm"
                                data-testid="admin-comment-submit">
                            {submitting ? "Posting…" : "Post comment"}
                        </button>
                    </div>
                </form>
            </div>
        </section>
    );
}
